/** @jsxRuntime classic */
/** @jsx jsx */
import { gql } from '@apollo/client';
import { Trans } from '@lingui/macro';
import '@reach/dialog/styles.css';
import { graphql } from 'gatsby';
import { CalendarBlank } from 'phosphor-react';
import { useState } from 'react';
import { jsx } from 'theme-ui';
import useLocale from '../../useLocale';
import type { ActionButtonProps } from '../ActionButton';
import ActionButton from '../ActionButton';
import { useCollection } from '../SiteSearch/useEventData';
import useSiteConfig from '../useSiteConfig';
import type { RequestMeetingDialogData } from './RequestMeetingButton.Dialog';
import RequestMeetingDialog from './RequestMeetingButton.Dialog';

type RequestMeetingButtonProps = Omit<ActionButtonProps, 'children'> & {
  profile: RequestMeetingButtonData;
};

export default function RequestMeetingButton({
  profile,
  ...rest
}: RequestMeetingButtonProps) {
  const { collectionId } = useCollection();
  const { isMeetingBookingEnabled } = useSiteConfig();
  const locale = useLocale();
  const [isOpen, setOpen] = useState(false);
  if (!isMeetingBookingEnabled) return null;

  return (
    <ActionButton onClick={() => setOpen(true)} {...rest}>
      <CalendarBlank size={16} weight="light" />
      <Trans>Meet</Trans>
      {isOpen && (
        <RequestMeetingDialog
          collectionId={collectionId}
          locale={locale || 'en'}
          profile={profile}
          onDismiss={() => setOpen(false)}
          isOpen={isOpen}
        />
      )}
    </ActionButton>
  );
}

export type RequestMeetingButtonData = RequestMeetingDialogData & {};

export function RequestMeetingButtonSkeleton(
  rest: Omit<ActionButtonProps, 'children'>
) {
  const { isChatEnabled } = useSiteConfig();
  if (!isChatEnabled) return null;
  return (
    <ActionButton disabled {...rest}>
      <CalendarBlank size={16} weight="light" />
      <Trans>Meet</Trans>
    </ActionButton>
  );
}

RequestMeetingButton.fragment = gql`
  fragment RequestMeetingButton on SiteUserProfile {
    ...RequestMeetingDialog
  }
  ${RequestMeetingDialog.fragment}
`;

export const fragment = graphql`
  fragment RequestMeetingButton on Bottlebooks_SiteUserProfile {
    ...RequestMeetingDialog
  }
`;
